import {CLIENT_MESSAGE, SERVER_MESSAGE} from "@redbrick-sdk/multiplay-common";
import * as Colyseus from "colyseus.js";
import * as THREE from "three";
import {clamp, lerp} from "three/src/math/MathUtils";
import {AVATAR_ANIMATION, URL} from "utils/constants";
import Avatar from "./Assets/Avatar.js";
import {PID} from "utils";

class GameServer {
  constructor(context) {
    this.context = context;
    this.serverStatus = true;
    this.avatarReady = false;
    this.serverReady = false;
    this.clientReady = false;
    this.prevPos = {x: 0, y: 0, z: 0};
    this.prevRot = {x: 0, y: 0, z: 0};
    this.timeOffset = 0;
    this.connectToServer();

    this._vector1 = new THREE.Vector3();
    this._vector2 = new THREE.Vector3();
    this._quaternion1 = new THREE.Quaternion();
    this._quaternion2 = new THREE.Quaternion();
    this._euler = new THREE.Euler();
  }

  async connectToServer() {
    this.client = new Colyseus.Client(URL.COLYSEUS);
    // [ Local Test ]
    // this.client = new Colyseus.Client("ws://localhost:2567");
  }

  async joinOrCreateRoom() {
    try {
      this.rooms = await this.client.getAvailableRooms("my_room");
      // TMP code to match the room and pid
      const pIdLength = this.context.pId.length;
      let roomId = null;
      for (let i = 0; i < this.rooms.length; i++) {
        if (this.rooms[i].roomId.substring(0, pIdLength) === this.context.pId) {
          roomId = this.rooms[i].roomId;
          break;
        }
      }
      let maxClients = 20;

      // For Gstar UGC Contents
      if (this.context.scenePlay.userData.gStar && this.context.scenePlay.userData.gStar.maxClients > 1) {
        maxClients = this.context.scenePlay.userData.gStar.maxClients;
      }

      if (!roomId) {
        let options = {
          pid: this.context.pId,
          url: URL.S3_WORLD_STORAGE + "/" + this.context.pId + ".json",
          emote: null,
          maxClients: maxClients,
          token: localStorage.getItem("redbrickToken"),
        };

        // fake server fetch progress
        let imitateProgress = 0;
        const imitateProgressInterval = setInterval(() => {
          if (imitateProgress < 99) {
            this.context.dispatchEvent(
              new CustomEvent("LOADING_IN_PROGRESS", {
                detail: {
                  object: {
                    progress: imitateProgress,
                    processName: "SERVER_SCENE_FETCH",
                  },
                },
              }),
            );
            imitateProgress += 1;
          }
        }, 32);
        this.room = await this.client.create("my_room", options);
        clearInterval(imitateProgressInterval);
        this.context.dispatchEvent(
          new CustomEvent("LOADING_IN_PROGRESS", {
            detail: {
              object: {progress: 100, processName: "SERVER_SCENE_FETCH"},
            },
          }),
        );
        console.log("[Server]: create room ", this.context.pId, " ok.");
      } else {
        this.room = await this.client.joinById(roomId);
        console.log("[Server]: join room ", roomId, " ok.");
      }
      this.apiMessageHandler();
      this.serverMessageHandler();
      this.stateEventHandler();
    } catch (e) {
      this.serverStatus = false;
      console.error(e);
    }
  }

  sendInputToServer(type, input) {
    if (!this.room) return;

    const message = {};
    switch (type) {
      case "move":
        if (!this.context.avatar.model.body) return;
        const {x: px, y: py, z: pz} = this.context.avatar.model.body.position;
        const {x: rx, y: ry, z: rz} = this.context.avatar.model.body.rotation;
        if (
          this.prevPos.x !== px ||
          this.prevPos.y !== py ||
          this.prevPos.z !== pz ||
          this.prevRot.x !== rx ||
          this.prevRot.y !== ry ||
          this.prevRot.z !== rz
        ) {
          this.prevPos = {x: px, y: py, z: pz};
          this.prevRot = {x: rx, y: ry, z: rz};
          this.room.send(CLIENT_MESSAGE.MOVE_INPUT, {
            pos: {x: px, y: py, z: pz},
            rot: {x: rx, y: ry, z: rz},
          });
        }
        break;
      case "key":
        message["keyCode"] = input.keyCode;
        message["isPressed"] = input.isPressed;
        this.room.send(CLIENT_MESSAGE.KEY_INPUT, message);
        break;
      case "mouse":
        // [TODO] 단순히 클릭을 했는지만 보낼지, 어떤 오브젝트를 클릭했는지도 보낼지 정해야 함
        message["target"] = input.target;
        this.room.send(CLIENT_MESSAGE.MOUSE_INPUT, message);
        break;
      default:
        break;
    }
  }

  sendAnimationMessage(animationName) {
    this.room.send(CLIENT_MESSAGE.SEND_ANIMATION, {animationName});
  }

  serverMessageHandler() {
    this.room.onMessage(SERVER_MESSAGE.SERVER_READY, () => {
      this.requestAvatarSetup(true, false);
    });
    this.room.onMessage(SERVER_MESSAGE.BROADCAST_ANIMATION, message => {
      const target = this.context.syncObjects[`avatar(${message.id})`];
      if (!target) return;
      target.manager.playAnimation(message.animationName);
    });
    this.room.onMessage(SERVER_MESSAGE.AVATAR_MODEL_CHANGED, () => {});
    this.room.onMessage(SERVER_MESSAGE.AVATAR_READY, () => {
      console.log("[Server]: My Avatar is Ready");
      this.avatarReady = true;
    });
    this.room.onMessage("userscript_error", message => {
      let alertMessage = "-- SCRIPT ERROR --";
      message.forEach(msg => {
        alertMessage += `\n[${msg[0]}] ${msg[1]}`;
      });
      alert(alertMessage);
    });
    this.room.onMessage(CLIENT_MESSAGE.AVATAR_EMOTE_CHANGED, message => {
      const target = this.context.syncObjects[`avatar(${message.id})`];
      if (!target) return;
      target.manager.emoteAnimation = message.info;
    });
  }

  stateEventHandler() {
    this.room.state.syncObjects.onAdd = this.onObjectStateAdded.bind(this);
    this.room.state.syncObjects.onRemove = this.onObjectStateRemoved.bind(this);
    this.room.onStateChange(state => {
      this.timeOffset = 0;
    });
  }

  requestAvatarSetup(serverReady, clientReady) {
    if (serverReady === true) this.serverReady = true;
    if (clientReady === true) this.clientReady = true;
    if (this.serverReady === true && this.clientReady === true) {
      const token = localStorage.getItem("redbrickToken");
      const ci = this.context.userInfo.ci;
      const options = {
        token: token,
        // [TEMP] for ipx...
        avatarId: this.context.fromIpx ? "ipx" : this.context.userInfo.avatarInfo[0].id,
        userName: this.context.userInfo.name,
        ci: ci,
      };
      this.room.send(CLIENT_MESSAGE.CREATE_AVATAR, options);
    }
  }

  async onObjectStateAdded(objectState, key) {
    if (objectState.type === "Light") return;
    if (typeof this["handleOnChange" + objectState.type + "State"] === "function") {
      if (objectState.type === "Avatar") {
        const avatar = await this.createAvatarByState(objectState, key);

        avatar.name = key;
        avatar.title = key;
        avatar.userData.type = "avatar";
        avatar.stateBuffer = [];
        this.context.syncObjects[key] = avatar;

        if (!this.context.playerStates) this.context.playerStates = {};
        this.context.playerStates[key] = objectState;
      } else if (objectState.type === "Camera") {
        this.context.playCamera.stateBuffer = [];
        this.context.syncObjects[key] = this.context.playCamera;
      }

      objectState.onChange = this["handleOnChange" + objectState.type + "State"].bind(this, this.context.syncObjects[key], objectState);
    } else {
      console.error("Unknown state changed", objectState.type);
      return;
    }
  }

  onObjectStateRemoved(objectState, key) {
    if (objectState.type === "Avatar") {
      this.context.physics.destroy(this.context.syncObjects[key].body);
      this.context.syncObjects[key].manager.removeNickname();
      delete this.context.playerStates[key];
    }
    this.context.scenePlay.remove(this.context.syncObjects[key]);
    delete this.context.syncObjects[key];
  }

  async createAvatarByState(state, key) {
    if (key === `avatar(${this.room.sessionId})`) {
      // [TMP] to run OOBC script in multiplay
      if (this.context.scenePlay.userData.codeEditorMode === "oobc") {
        Avatar.instance.model.body.name = key;
        await this.context.runScript();
      }

      return Avatar.instance.model;
    }

    const avatar = new Avatar(this.context, false);
    const {avatarId} = state.avatarModel;

    await avatar.loadForMulti(avatarId);
    await avatar.loadSocketMap();

    avatar.attachNickname(state.clientInfo.userName);

    this.context.scenePlay.add(avatar.model);

    return avatar.model;
  }

  handleOnChangeAvatarState(syncObject, state, changes) {
    changes.forEach(change => {
      switch (change.field) {
        case "clientInfo":
          break;
        case "avatarModel":
          state.avatarModel.onChange = this.handleOnChangeAvatarModelState.bind(this, syncObject.manager, state.avatarModel);
          break;
        case "speedFactor":
          syncObject.manager.speedFactor = change.value;
          break;
        case "deltaPosDefault":
          break;
        default:
          if (state.title === `avatar(${this.room.sessionId})` && change.field === "ts") break;
          this.commonObjectStateIterator(syncObject, state, change);
          break;
      }
    });
  }

  handleOnChangeAvatarModelState(avatarModel, state, changes) {
    let avatarId;

    changes.forEach(change => {
      switch (change.field) {
        case "avatarId":
          avatarId = change.value;
          break;
        default:
          break;
      }
    });

    avatarModel.changeLook(avatarId);
  }

  commonObjectStateIterator(syncObject, state, change) {
    switch (change.field) {
      case "ts":
        const buffer = {
          ts: state.ts,
          pos: {...state.position},
          rot: {...state.rotation},
          ignorePositionLerping:
            [...state.forcedPositionTS].filter(ts => ts > syncObject.stateBuffer[syncObject.stateBuffer.length - 1]?.ts).length > 0,
          ignoreRotationLerping:
            [...state.forcedRotationTS].filter(ts => ts > syncObject.stateBuffer[syncObject.stateBuffer.length - 1]?.ts).length > 0,
        };
        syncObject.stateBuffer.push(buffer);
        break;
      case "castShadow":
        syncObject.traverse(child => (child.castShadow = change.value));
        break;
      case "receiveShadow":
        syncObject.traverse(child => (child.receiveShadow = change.value));
        break;
      case "collisionFlag":
        break;
      case "layerMask":
        break;
      case "isAlive":
        if (syncObject.userData.playInfo === undefined) return;
        if (change.value === true) {
          if (syncObject.userData.playInfo.isAlive === true) return;
          syncObject.userData.playInfo.isAlive = true;
          syncObject.traverse(child => {
            child.layers.mask = syncObject.userData.playInfo.layerMask;
          });
          if (syncObject.body) {
            syncObject.body.setCollisionFlags(syncObject.userData.playInfo.collisionFlag);
          }
        } else {
          if (syncObject.userData.playInfo.isAlive === false) return;
          syncObject.userData.playInfo.isAlive = false;
          syncObject.userData.playInfo.layerMask = syncObject.layers.mask;
          syncObject.traverse(child => {
            child.layers.disableAll();
            if (child.type === "Audio") child.stop();
          });
          if (syncObject.body) {
            syncObject.userData.playInfo.collisionFlag = syncObject.body.getCollisionFlags();
            syncObject.body.setCollisionFlags(5);
          }
        }
        break;
      case "audio":
        const audio = syncObject.getObjectByProperty("type", "Audio");
        if (audio && state.audio) {
          state.audio.onChange = this.handleOnChangeAudioState.bind(this, audio, state.audio);
        }
        break;
      default:
        break;
    }
  }

  handleOnChangeCommonObjectState(syncObject, state, changes) {
    changes.forEach(change => {
      this.commonObjectStateIterator(syncObject, state, change);
    });
  }

  // handleOnChangeLightState(syncObject, state, changes) {
  //   changes.forEach(change => {
  //     switch (change.field) {
  //       default:
  //         this.commonObjectStateIterator(syncObject, state, change);
  //         break;
  //     }
  //   });
  // }

  handleOnChangeCameraState(syncObject, state, changes) {
    changes.forEach(change => {
      switch (change.field) {
        case "fov":
          this.context.playCamera.fov = change.value;
          break;
        case "near":
          this.context.playCamera.near = change.value;
          break;
        case "far":
          this.context.playCamera.far = change.value;
          break;
        case "minZoom":
          this.context.playControls.minZoom && (this.context.playControls.minZoom = change.value);
          break;
        case "maxZoom":
          this.context.playControls.maxZoom && (this.context.playControls.maxZoom = change.value);
          break;
        case "toggle":
          // this.playCamera.layers.toggle(change.value);
          break;
        default:
          this.commonObjectStateIterator(syncObject, state, change);
          break;
      }
    });
  }

  handleOnChangeMeshState(syncObject, state, changes) {
    changes.forEach(change => {
      switch (change.field) {
        case "material":
          const material = syncObject.getObjectByProperty("isMesh", true).material;
          if (material) {
            state.material.onChange = this.handleOnChangeMaterialState.bind(this, material, state.material);
          }
          break;
        default:
          this.commonObjectStateIterator(syncObject, state, change);
          break;
      }
    });
  }

  handleOnChangeParticleSystemState(syncObject, state, changes) {
    changes.forEach(change => {
      switch (change.field) {
        case "isEmit":
          if (change.value === true) {
            const system = this.context.particleSystemArray.find(system => system.emitters[0].id === syncObject.emitterId);
            system.emitters[0].emit(Infinity, Infinity);
          } else {
            const system = this.context.particleSystemArray.find(system => system.emitters[0].id === syncObject.emitterId);
            system.emitters[0].stopEmit();
            syncObject.children = [];
          }
          break;
        default:
          this.commonObjectStateIterator(syncObject, state, change);
          break;
      }
    });
  }

  handleOnChangePortalState(syncObject, state, changes) {
    changes.forEach(change => {
      switch (change.field) {
        default:
          this.commonObjectStateIterator(syncObject, state, change);
          break;
      }
    });
  }

  handleOnChangeSpriteState(syncObject, state, changes) {
    changes.forEach(change => {
      switch (change.field) {
        case "visible":
          syncObject.visible = change.value;
          break;
        case "text":
          const textElement = syncObject.getObjectByProperty("isCSS2DObject", true).element;
          if (textElement) {
            state.text.onChange = this.handleOnChangeTextState.bind(this, textElement, state.text);
          }
          break;
        default:
          this.commonObjectStateIterator(syncObject, state, change);
          break;
      }
    });
  }

  handleOnChangeSceneState(syncObject, state, changes) {
    changes.forEach(change => {
      switch (change.field) {
        case "background":
          const background = syncObject.background;
          if (background) {
            state.background.onChange = this.handleOnChangeBackgroundState.bind(this, syncObject, state.background);
          }
          break;
        default:
          this.commonObjectStateIterator(syncObject, state, change);
          break;
      }
    });
  }

  handleOnChangeVideoState(syncObject, state, changes) {
    changes.forEach(change => {
      switch (change.field) {
        case "isVideoPlaying":
          if (change.value === true) {
            this.context.videoElements[syncObject.uuid].play();
          } else if (change.value === false) {
            this.context.videoElements[syncObject.uuid].pause();
          }
          break;
        default:
          this.commonObjectStateIterator(syncObject, state, change);
          break;
      }
    });
  }

  handleOnChangeAudioState(audio, state, changes) {
    changes.forEach(change => {
      switch (change.field) {
        case "playingStatus":
          if (change.value === 0) {
            audio.isPlaying && audio.stop();
          } else if (change.value === 1) {
            audio.isPlaying && audio.pause();
          } else if (change.value === 2) {
            audio.isPlaying || audio.play();
          } else {
            new Promise((resolve, reject) => {
              audio.stop();
              resolve();
            }).then(() => audio.play());
          }
          break;
        case "volume":
          audio.setVolume(clamp(change.value, 0, 10));
          break;
        case "playbackRate":
          audio.setPlaybackRate(clamp(change.value, 0.1, 5));
          break;
        default:
          break;
      }
    });
  }

  handleOnChangeBackgroundState(scene, state, changes) {
    changes.forEach(change => {
      switch (change.field) {
        case "backgroundType":
          if (change.value === "color") {
            scene.background = new THREE.Color(Number(state.backgroundValue));
          } else if (change.value === "cubeTexture") {
            let skyPath = `https://d2f4dt8igm02oj.cloudfront.net/redbrick-asset/cubeTexture/${state.backgroundValue}/${state.backgroundValue}`;
            let sky = {
              SKY: [
                skyPath + "_px.png",
                skyPath + "_nx.png",
                skyPath + "_py.png",
                skyPath + "_ny.png",
                skyPath + "_pz.png",
                skyPath + "_nz.png",
              ],
            };
            scene.background = this.context.cubeTextureLoader.load(sky.SKY, () => {
              scene.background.name = `${state.backgroundValue}`;
              scene.background.key = scene.background.uuid;
              scene.background.title = `${state.backgroundValue}`;
              scene.background.userData = {type: "sky"};
              scene.background.encoding = THREE.sRGBEncoding;
              scene.background.needsUpdate = true;
            });
          }
          break;
        default:
          break;
      }
    });
  }

  handleOnChangeMaterialState(material, state, changes) {
    changes.forEach(change => {
      switch (change.field) {
        case "map":
          const map = material.map;
          if (map) {
            state.map.onChange = this.handleOnChangeTextureState.bind(this, map, state.map);
          }
          break;
        default:
          break;
      }
    });
  }

  handleOnChangeTextState(textElement, state, changes) {
    changes.forEach(change => {
      switch (change.field) {
        case "textContent":
          textElement.textContent = change.value;
          break;
        // case "fontColor":
        //   textElement.style.color = change.value;
        //   break;
        case "fontSize":
          textElement.style.fontSize = `${change.value}px`;
          break;
        // case "fontFamily":
        //   textElement.style.fontFamily = change.value;
        //   break;
        // case "fontWeight":
        //   textElement.style.fontWeight = change.value;
        //   break;
        default:
          break;
      }
    });
  }

  handleOnChangeTextureState(texture, state, changes) {
    changes.forEach(change => {
      switch (change.field) {
        case "name":
          // TODO
          break;
        case "offset":
          state.offset.onChange = this.handleOnChangeVector2State.bind(this, texture.offset, state.offset);
          break;
        default:
          break;
      }
    });
  }

  handleOnChangeVector2State(vector2, state, changes) {
    changes.forEach(change => {
      switch (change.field) {
        case "x":
          vector2.setX(change.value);
          break;
        case "y":
          vector2.setY(change.value);
          break;
        default:
          break;
      }
    });
  }

  updateSyncObjectsMovement(dt) {
    if (this.context.syncObjects === undefined) return;

    let animationName;

    this.timeOffset += 1000 * dt;
    // below formula comes from : ((1 + 3 / 60 / dt) / 4) * 0.15 * this.timeOffset
    const timeFactor = (0.0375 + 0.001875 / dt) * this.timeOffset;
    let alpha = clamp(dt * (5 + timeFactor), 0, 1);

    Object.values(this.context.syncObjects).forEach(object => {
      if (!object.body) {
        // [TEMP] light objects don't have body, but need to move, too.
        object.stateBuffer = [];
        return;
      }

      while (object.stateBuffer.length >= 3) {
        object.stateBuffer.splice(0, 1);
      }
      if (object.stateBuffer.length >= 2) {
        const posAlpha = object.stateBuffer[1].ignorePositionLerping ? 1 : alpha;
        const rotAlpha = object.stateBuffer[1].ignoreRotationLerping ? 1 : alpha;

        object.body.transform();

        const {x: px1, y: py1, z: pz1} = object.body.position;
        const {x: rx1, y: ry1, z: rz1} = object.body.rotation;
        const {x: px2, y: py2, z: pz2} = object.stateBuffer[1].pos;
        const {x: rx2, y: ry2, z: rz2} = object.stateBuffer[1].rot;

        // Other avatars & Objects
        const newPosX = lerp(px1, px2, posAlpha);
        const newPosY = lerp(py1, py2, posAlpha);
        const newPosZ = lerp(pz1, pz2, posAlpha);
        object.body.setPosition(newPosX, newPosY, newPosZ);

        this._quaternion1.copy(object.quaternion);
        this._euler.set(rx2, ry2, rz2);
        this._quaternion2.setFromEuler(this._euler);
        this._quaternion1.slerp(this._quaternion2, rotAlpha);
        this._euler.setFromQuaternion(this._quaternion1);
        object.body.setRotation(this._euler.x, this._euler.y, this._euler.z);

        object.body.refresh();

        if (object.userData.type === "avatar") {
          object.manager.render(dt);
          this._quaternion1.copy(object.quaternion);
          this._euler.set(rx2, ry2, rz2);
          this._quaternion2.setFromEuler(this._euler);
          this._quaternion1.slerp(this._quaternion2, alpha);
          this._euler.setFromQuaternion(this._quaternion1);
          object.body.setRotation(this._euler.x, this._euler.y, this._euler.z);
          // object.getWorldDirection(this._vector1);
          // this._vector2.set(px2 - px1, 0, pz2 - pz1);
          // if (this._vector2.lengthSq() > 0.01) {
          //   let angle = this._vector1.angleTo(this._vector2);
          //   angle *=
          //     this._vector1.cross(this._vector2).y > 0 ? 1 : -1;
          //   const newRotY = ry1 + lerp(0, angle, alpha / 2);
          //   object.body.setRotation(0, newRotY, 0);
          // }
          if (object.manager.isJumpAnimationPlaying === false || object.manager.isJumpAnimationPlaying === undefined) {
            if (
              object.stateBuffer[1].pos.x === object.stateBuffer[0].pos.x &&
              object.stateBuffer[1].pos.y === object.stateBuffer[0].pos.y &&
              object.stateBuffer[1].pos.z === object.stateBuffer[0].pos.z
            ) {
              animationName = AVATAR_ANIMATION.IDLE;
              const emote = object.manager.emoteAnimation;
              if (emote) {
                animationName = emote;
              }
            } else {
              if (object.manager.speedFactor > 1) animationName = AVATAR_ANIMATION.BOOST;
              else animationName = AVATAR_ANIMATION.RUN;
            }
            object.manager.playAnimation(animationName);
          }
          object.manager.animationMixer.update(dt);
        }
      }
    });
  }

  // TEMP - RPC
  apiMessageHandler() {
    this.room.onMessage("api", message => {
      let subject;
      if (message.title) {
        subject =
          this.context.scenePlay.getObjectByProperty("title", message.title) ||
          this.context.sceneGUI.getObjectByProperty("title", message.title);
      }

      if (message.api === "setLayer") {
        this.context.avatar.model.setLayer(2);
        return;
      }

      if (subject) {
        const params = Object.values(message.params);
        const ret = subject[message.api](...params, {syncAll: false});
        if (message.requestId) {
          this.room.send(CLIENT_MESSAGE.API_RESULT, {
            title: message.title,
            requestId: message.requestId,
            params: ret,
          });
        }
      } else if (this.context.scenePlay[message.api]) {
        const params = Object.values(message.params);
        this.context.scenePlay[message.api](...params, {syncAll: false});
      } else {
        console.error(`${message.api} handler is not defined`);
      }
    });
  }
}

export default GameServer;
